/*
 * StackSchematic overlay: the Graph product's sidebar link, and one that lands on something.
 *
 * (The component keeps its name — it wraps Backstage's Catalog Graph page — but the item reads
 * "Graph" in the product cluster. ProductNav renders it for the Graph slot.)
 *
 * WHY THIS EXISTS
 *
 * `CatalogGraphPage` draws the graph outward from a set of root entities. It
 * reads those roots from the URL (`?rootEntityRefs[]=...`), which is how the
 * "View graph" button on an entity page reaches it. The sidebar item that the
 * frontend system generates from the page extension carries only the bare path
 * `/catalog-graph`, so the roots are `[]` and the page renders filters over an
 * empty canvas. That is upstream behaviour, not a defect in the catalog data.
 *
 * The obvious fix - setting `rootEntityRefs` in the extension's config - does
 * not work in @backstage/plugin-catalog-graph 0.6.6. Its config schema declares
 * those keys flat and spreads them as flat props, while the component reads them
 * only from `props.initialState`, so they are accepted and then dropped. See the
 * "Upstream wiring bug" section of the overlay README.
 *
 * So the link supplies the roots itself. The roots are DISCOVERED at runtime -
 * every Domain in the catalog, falling back to every System - so this stays
 * generic: it contains no knowledge of any particular catalog, and in an install
 * with neither kind it degrades to the upstream link.
 */
import { SidebarItem } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEffect, useState } from 'react';

import { graphHref } from './graphHref';
import { ProductGlyph } from '../apps/ProductGlyph';

// Rendered inside the flat product cluster (ProductNav), so it wears the family's Graph glyph
// rather than a borrowed Material icon — otherwise Graph is the one stock icon in a row of glyphs.
const GraphGlyphIcon = () => <ProductGlyph product="Graph" size={20} />;

/** Entity refs of every Domain, or every System if the catalog has no domains. */
function useEstateRoots(): string[] {
  const catalogApi = useApi(catalogApiRef);
  const [roots, setRoots] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    const refsOfKind = async (kind: string) => {
      const { items } = await catalogApi.getEntities({
        filter: { kind },
        fields: ['kind', 'metadata.name', 'metadata.namespace'],
      });
      return items.map(
        e =>
          `${e.kind.toLocaleLowerCase('en-US')}:${
            e.metadata.namespace ?? 'default'
          }/${e.metadata.name}`,
      );
    };

    (async () => {
      const domains = await refsOfKind('domain');
      const found = domains.length > 0 ? domains : await refsOfKind('system');
      if (!cancelled) {
        setRoots(found);
      }
    })().catch(() => {
      // A rootless link is the upstream behaviour: a worse landing page, not a
      // broken one. Failing to build a nicer href must not remove the item.
    });

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return roots;
}

export const CatalogGraphNavItem = () => {
  const roots = useEstateRoots();
  return (
    <SidebarItem
      icon={GraphGlyphIcon}
      to={graphHref(roots)}
      text="Graph"
    />
  );
};
